'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import style from './Footer.module.scss';
import SmoothScrollLink from '../SmoothScrollLink/SmoothScrollLink';
import { Typography } from '../Typography';
import { sanitizeRichText } from '../../utils/sanitizeHtml';

interface AboutData {
  address: string;
  phone: string;
  email?: string;
}

interface FooterProps {
  aboutData?: AboutData[];
}

const Footer: React.FC<FooterProps> = ({ aboutData }) => {
  const [currentYear, setCurrentYear] = useState(2025);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setCurrentYear(new Date().getFullYear());
    setIsMounted(true);
  }, []);

  return (
    <footer className={style.footer}>
      <div className={style.footerContainer}>
        {/* Footer Content */}
        <div className={style.footerContent}>
          {/* Brand Section */}
          <div className={style.brandSection}>
            <Link href="/" aria-label="Epen Home">
              <a className={style.logo}>
                {/* .logo is display:flex, so next/image's responsive wrapper
                    (needs a block-level parent for 100% width) needs this
                    explicit-width div or it collapses to 0 in the flex box. */}
                <div style={{ width: '100%' }}>
                  <Image
                    src="/epenlogo.png"
                    alt="Epen Logo"
                    width={120}
                    height={40}
                    layout="responsive"
                  />
                </div>
              </a>
            </Link>

            <div className={style.subTitle}>
              <Typography
                variant="text-sm"
                as="p"
                color="secondary"
                weight="normal"
                fontSize="14px"
              >
                Empowering creators with innovative video editing solutions and cutting-edge
                technologies.
              </Typography>
            </div>
          </div>

          {/* Links Container */}
          <div className={style.links}>
            {/* Products Section */}
            <div className={style.linkGroup}>
              <div className={style.heading}>
                <Typography
                  variant="text-lg"
                  as="h3"
                  color="primary"
                  weight="semibold"
                  fontSize="18px"
                >
                  Product
                </Typography>
                <div className={style.line9}></div>
              </div>

              <div className={style.linkContainer}>
                <Link href="/video-plugin-framework/vxfx">
                  <Typography
                    as="a"
                    variant="text-sm"
                    color="secondary"
                    weight="normal"
                    fontSize="14px"
                    className={`${style.navLink} ${style.navLinkText}`}
                  >
                    VxFx
                  </Typography>
                </Link>
              </div>
            </div>

            {/* Quick Links Section */}
            <div className={style.linkGroup}>
              <div className={style.heading}>
                <Typography
                  variant="text-lg"
                  as="h3"
                  color="primary"
                  weight="semibold"
                  fontSize="18px"
                >
                  Quick Links
                </Typography>
                <div className={style.line9}></div>
              </div>

              <div className={style.linkContainer}>
                <Link href="/contact-us">
                  <Typography
                    as="a"
                    variant="text-sm"
                    color="secondary"
                    weight="normal"
                    fontSize="14px"
                    className={`${style.navLink} ${style.navLinkText}`}
                  >
                    Contact Us
                  </Typography>
                </Link>
                <Link href="/terms-and-conditions">
                  <Typography
                    as="a"
                    variant="text-sm"
                    color="secondary"
                    weight="normal"
                    fontSize="14px"
                    className={`${style.navLink} ${style.navLinkText}`}
                  >
                    Terms of Service
                  </Typography>
                </Link>
                <Link href="/faq">
                  <Typography
                    as="a"
                    variant="text-sm"
                    color="secondary"
                    weight="normal"
                    fontSize="14px"
                    className={`${style.navLink} ${style.navLinkText}`}
                  >
                    FAQ
                  </Typography>
                </Link>
                <Link href="/privacy-policy">
                  <Typography
                    as="a"
                    variant="text-sm"
                    color="secondary"
                    weight="normal"
                    fontSize="14px"
                    className={`${style.navLink} ${style.navLinkText}`}
                  >
                    Privacy Policy
                  </Typography>
                </Link>
              </div>
            </div>

            {/* Get In Touch Section */}
            <div className={style.linkGroup}>
              <div className={style.heading}>
                <Typography
                  variant="text-lg"
                  as="h3"
                  color="primary"
                  weight="semibold"
                  fontSize="18px"
                >
                  Get In Touch
                </Typography>
                <div className={style.line9}></div>
              </div>

              {aboutData?.map((item, index) => (
                <div key={index} className={style.linkContainer}>
                  {item.phone && (
                    <div className={style.contactLink}>
                      <img src="/phone.svg" loading="lazy" alt="phone" style={{ marginTop: 2 }} />
                      <a href={`tel:${item.phone}`}>
                        <Typography
                          variant="text-sm"
                          as="div"
                          color="secondary"
                          weight="normal"
                          fontSize="14px"
                          className={style.contactText}
                        >
                          {item.phone}
                        </Typography>
                      </a>
                    </div>
                  )}
                  {item.email && (
                    <div className={style.contactLink}>
                      <img src="/email.svg" loading="lazy" alt="email" style={{ marginTop: 2 }} />
                      <a href={`mailto:${item.email}`}>
                        <Typography
                          variant="text-sm"
                          color="secondary"
                          weight="normal"
                          fontSize="14px"
                          className={style.contactText}
                        >
                          {item.email}
                        </Typography>
                      </a>
                    </div>
                  )}
                  {item.address && (
                    <div className={style.contactLink}>
                      <img
                        src="/location.svg"
                        loading="lazy"
                        alt="location"
                        style={{ marginTop: 2 }}
                      />
                      {isMounted ? (
                        <Typography
                          variant="text-sm"
                          color="secondary"
                          weight="normal"
                          fontSize="14px"
                          style={{ cursor: 'auto' }}
                          className={style.contactText}
                          dangerouslySetInnerHTML={{
                            __html: sanitizeRichText(item.address),
                          }}
                        />
                      ) : (
                        <Typography
                          variant="text-sm"
                          color="secondary"
                          weight="normal"
                          fontSize="14px"
                          style={{ cursor: 'auto' }}
                          className={style.contactText}
                        >
                          {item.address.replace(/<[^>]*>/g, '')}
                        </Typography>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className={style.divider}>
          <div className={style.line8}></div>
        </div>

        {/* Lower Section */}
        <div className={style.lower}>
          {/* Social Media Links - Hidden for now */}
          {/* <div className={style.social}>
            <a href="#" className={style.socialLink} aria-label="Instagram">
              <img src="/instagramLogo.svg" loading="lazy" alt="instagram" />
            </a>
            <a href="#" className={style.socialLink} aria-label="Facebook">
              <img src="/facebookLogo.svg" loading="lazy" alt="facebook" />
            </a>
            <a href="#" className={style.socialLink} aria-label="YouTube">
              <img src="/youtubeLogo.svg" loading="lazy" alt="youtube" />
            </a>
            <a href="#" className={style.socialLink} aria-label="Twitter">
              <img src="/twitterLogo.svg" loading="lazy" alt="twitter" />
            </a>
            <a href="#" className={style.socialLink} aria-label="LinkedIn">
              <img src="/linkedinLogo.svg" loading="lazy" alt="linkedin" />
            </a>
          </div> */}

          <div className={style.legal}>
            <Typography
              variant="text-xs"
              as="p"
              color="disabled"
              weight="normal"
              className={style.copyright}
            >
              © {currentYear} Epen Ltd. All Rights Reserved.
            </Typography>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
